import { Box, Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/material';
import { t } from 'i18next';
import { KeyboardEvent, SyntheticEvent } from 'react';

import { FormField, IAutocompleteProps } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { IQueryPageData } from 'core/types';
import { ExternalHazardousIcon, ExternalMarketingMemo, ExternalPartStatus } from 'parts/elements';
import { ExternalPartStatusType } from 'parts/enums';
import { PartNumberHelper } from 'parts/helpers';
import { partService } from 'parts/services';
import { IPartAutocomplete } from 'parts/types';
import { Color, Level } from 'theme/enums';

import PopupIndicator from './PopupIndicator';
import { descriptionStyle, endDecoratorStyle, inputStyle } from './style';

interface IProps extends Omit<IAutocompleteProps, 'onChange' | 'onKeyDown'> {
  name: string;
  optionKey?: string;
  label?: string;
  placeholder?: string;
  onChange?: (event: SyntheticEvent, value: IPartAutocomplete) => void;
  onBlur?: (event: SyntheticEvent) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>, search: string) => void;
  endDecorator?: JSX.Element;
  onEndDecoratorClick?: (search?: string) => void;
  disabled?: boolean;
  listboxStyle?: SxProps;
  autocompleteStyle?: SxProps;
  requestPayload?: Record<string, unknown>;
  isAlternateVersionEnabled?: boolean;
}

const PAGE_SIZE = 10;
const MIN_CHARS_TO_SEARCH = 2;

const PartNumberFormField = ({
  name,
  optionKey = 'number',
  label = '',
  placeholder = t('labelPartNumber'),
  onChange,
  onBlur,
  onKeyDown,
  endDecorator,
  onEndDecoratorClick,
  disabled = false,
  listboxStyle,
  autocompleteStyle,
  requestPayload = {},
  isAlternateVersionEnabled,
  ...rest
}: IProps) => {
  const renderOptions = (
    option: IPartAutocomplete,
    _: (option: IPartAutocomplete) => string,
    inputValue: string
  ) => {
    const hasNumber = option?.[optionKey]?.length > 0;

    const showCompanyPartNumber =
      option?.vendorNumber && option?.vendorNumber !== option?.[optionKey];

    const optionTitle =
      'number' === optionKey ? PartNumberHelper.toLabel(option?.number) : option?.[optionKey];

    const titleSx =
      isAlternateVersionEnabled && option?.externalPartStatus === ExternalPartStatusType.ABANDONED
        ? { textDecoration: 'line-through' }
        : {};

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {hasNumber ? (
            <Typography
              level={Level.TITLE_SM}
              sx={titleSx}
              startDecorator={
                isAlternateVersionEnabled &&
                option.hazardous && <ExternalHazardousIcon isHazardous />
              }
            >
              {<TextHighlighter text={optionTitle} highlight={inputValue} />}
            </Typography>
          ) : null}
          <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
            {showCompanyPartNumber ? (
              <NeutralChip>
                <TextHighlighter text={option.vendorNumber} highlight={inputValue} />
              </NeutralChip>
            ) : null}
          </Typography>
        </Box>
        {isAlternateVersionEnabled && (
          <Stack sx={{ flex: 1, gap: 0.5, padding: '4px 0' }}>
            <ExternalPartStatus value={option?.externalPartStatus} displayWarning />
            <ExternalMarketingMemo marketingMemo={option?.marketingMemo} />
          </Stack>
        )}
        <Typography level={Level.BODY_XS}>
          <TextHighlighter
            text={option?.descriptionLong || DEFAULT_PLACEHOLDER}
            highlight={inputValue}
            sx={descriptionStyle}
          />
        </Typography>
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {option?.manufacturerName}
        </Typography>
      </Box>
    );
  };

  const getNextPageParam = (lastPage: IQueryPageData, page: any[]) => {
    if (lastPage?.records?.length < PAGE_SIZE) {
      return undefined;
    }

    return page.length + 1;
  };

  return (
    <FormField
      name={name}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        label,
        optionKey,
        placeholder,
        endDecorator,
        onEndDecoratorClick,
        renderOptions,
        getOptions: partService.getManyAutocomplete,
        requestPayload: {
          manufacturerIncluded: 1,
          alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
          ...requestPayload,
        },
        queryOptions: { getNextPageParam },
        getKey: (option: IPartAutocomplete) =>
          `${option?.number}-${option?.manufacturerId}-${option?.vendorNumber}`,
        getLabel: (option: IPartAutocomplete) => PartNumberHelper.toLabel(option?.number),
        onChange,
        onBlur,
        onKeyDown,
        slots: {
          popupIndicator: endDecorator ? null : PopupIndicator,
        },
        listboxStyle,
        disabled,
        inputStyle,
        autocompleteStyle,
        endDecoratorStyle,
        showPopupIndicator: !endDecorator,
        pageSize: PAGE_SIZE,
        minCharsToSearch: MIN_CHARS_TO_SEARCH,
        applyDefaultQuery: true,
        ...rest,
      }}
    />
  );
};

export default PartNumberFormField;
